import {
    Box,
    BoxComponentProps,
    Container,
    ContainerProps,
    Divider,
    Group,
    GroupProps,
    MantineStyleProps,
    Paper,
    PolymorphicComponentProps,
    Space,
    Stack,
    StackProps,
    Title,
} from "@mantine/core";
import { ReactNode } from "react";
import { BackButton } from "shared/components/global/BackButton";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { isEmbedded } from "shared/interop";
import { some } from "shared/utils/maybe";
import { createMarkerComponent, createSlots } from "shared/utils/slots";

type ContentProps = {
    /**
     * Additonal props to pass to the underlying {@link Stack} wrapper
     */
    stack?: StackProps;
    paper?: boolean;
    /**
     * When set, allows any <Action /> components to wrap
     */
    actionWrap?: boolean;
} & PolymorphicComponentProps<"div", BoxComponentProps>;

/** @private */
const Heading = ({ backable, children, ...rest }: { backable?: boolean } & GroupProps) => {
    const slots = createSlots(children, {
        BackButton,
    });

    return (
        <Group py="md" {...rest}>
            {some(slots.BackButton).takeIf((it) => it.length > 0) ?? (backable && <BackButton />)}
            {typeof slots.rest.at(0) == "string" ? <Title order={3}>{slots.rest}</Title> : slots.rest}
        </Group>
    );
};

const Action = createMarkerComponent();
const Above = createMarkerComponent();
const Below = createMarkerComponent();

/**
 * A responsive {@link Container} wrapper. This is the default wrapper that should be used for all pages.
 */
export const Content = Object.assign(
    ({ stack, paper, actionWrap, children, ...rest }: ContentProps) => {
        const Wrapper = paper ? PaperWrapper : Box;
        const layoutProps = paper ? {} : rest;
        const isMobile = useIsMobile();

        const slots = createSlots(children, {
            Heading,
            Action,
            Above,
            Below,
        });

        const hasHeading = !!slots.Heading.length || !!slots.Action.length;

        if (isEmbedded())
            return (
                <Wrapper {...rest}>
                    <Stack {...stack}>{slots.rest}</Stack>
                </Wrapper>
            );

        return (
            <Box>
                <Layout {...layoutProps}>
                    {hasHeading && (
                        <Group w="100%" justify="space-between" gap={0}>
                            {slots.Heading}
                            {some(slots.Action)
                                .if((it) => !!it)
                                ?.take((it) => (
                                    <Group
                                        gap={0}
                                        flex="fit-content"
                                        justify={isMobile ? "space-between" : "flex-end"}
                                        wrap={actionWrap ? "wrap" : "nowrap"}
                                        style={{ rowGap: 0 }}
                                    >
                                        {it}
                                    </Group>
                                ))}
                        </Group>
                    )}
                    {isMobile && slots.Action.length > 0 && <Divider mt="xs" />}
                    {hasHeading && isMobile && <Space h="xs" />}
                    {slots.Above}
                    <Wrapper {...rest}>
                        <Stack {...stack}>{slots.rest}</Stack>
                    </Wrapper>
                    {slots.Below}
                </Layout>
            </Box>
        );
    },
    {
        /**
         * Children to be rendered within the Layout, but above and outside the Wrapper
         */
        Heading,
        Action,
        Above,
        Below,
    },
);

const Layout = ({ children, ...rest }: ContainerProps) => {
    const isMobile = useIsMobile();

    return (
        <Container size={"lg"} px={isMobile ? "sm" : undefined} {...rest}>
            {children}
        </Container>
    );
};

export const PaperWrapper = ({ children, ...styles }: { children: ReactNode } & MantineStyleProps) => {
    const isMobile = useIsMobile();
    return (
        <Paper p="sm" mb="lg" radius={isMobile ? 0 : "md"} withBorder {...styles}>
            {children}
        </Paper>
    );
};
